import { Box, Button, Checkbox, FormControlLabel, FormHelperText, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import styles from "./addUser.module.css";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { useCRUD } from "../../../api/crud";
import { USERS_URL } from "../../../api/users";
import { PERMISSION_GROUPS_URL } from "../../../api/permissions";
import { PermissionGroup } from "../../../types/permissions";
import CustomAutocomplete from "../../../components/ui/customAutocomplete/customAutocomplete.component";

interface AddUserProps {
  onSuccess?: () => void;
}

export const AddUser = ({ onSuccess }: AddUserProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, create, error } = useCRUD(USERS_URL);
  const { query: queryPermissionGroups, loading: groupsLoading } = useCRUD(PERMISSION_GROUPS_URL);

  const defaultFormData = {
    email: "",
    firstName: "",
    lastName: "",
    groups: [] as PermissionGroup[],
    receivesSubscriptionMessages: false,
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);

  useEffect(() => {
    const loadPermissionGroups = async () => {
      const data = await queryPermissionGroups({ page: 0, pageSize: 1000, sortModel: [], filterModel: { items: [] } });
      if (data?.results) {
        setPermissionGroups(data.results);
      }
    };
    loadPermissionGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddUserSubmit = async () => {
    const { success } = await create({
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      groups_ids: formData.groups.map((group) => group.id),
      receives_subscription_messages: formData.receivesSubscriptionMessages,
    });

    if (success) {
      window.dispatchEvent(new Event("tenantUpdated"));
      enqueueSnackbar(t("User added successfully"), SUCCESS_SNACKBAR);
      setFormData(defaultFormData);
      onSuccess?.();
    } else {
      enqueueSnackbar(t("User add failed"), ERROR_SNACKBAR);
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="firstName"
          label={t("First name")}
          size="small"
          fullWidth
          value={formData.firstName}
          onChange={handleFormChange}
          required
        />
        {error && error?.first_name && (
          <div className={styles.errorMessage}>
            {error.first_name[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="lastName"
          label={t("Last name")}
          size="small"
          fullWidth
          value={formData.lastName}
          onChange={handleFormChange}
          required
        />
        {error && error?.last_name && (
          <div className={styles.errorMessage}>
            {error.last_name[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="email"
          label={t("Email")}
          size="small"
          fullWidth
          value={formData.email}
          onChange={handleFormChange}
          required
        />
        {error && error?.email && (
          <div className={styles.errorMessage}>{error.email[0]?.message}</div>
        )}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <CustomAutocomplete
          multiple
          options={permissionGroups}
          getOptionLabel={(option) => option.name}
          value={formData.groups}
          onChange={(_, newValue) => {
            setFormData((prev) => ({ ...prev, groups: newValue }));
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={groupsLoading}
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Permission Groups")}
              placeholder={t("Select permission groups")}
            />
          )}
        />
        {error && error?.groups_ids && (
          <div className={styles.errorMessage}>
            {error.groups_ids[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ width: "100%" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.receivesSubscriptionMessages}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, receivesSubscriptionMessages: e.target.checked }))
              }
            />
          }
          label={t("Send subscription related messages")}
          sx={{ color: "text.primary" }}
        />
        <FormHelperText sx={{ mt: 0 }}>
          {t("Transfer details, invoices and payment reminders for the subscription are e-mailed to these users. At least one user must receive them.")}
        </FormHelperText>
        {error && error?.receives_subscription_messages && (
          <div className={styles.errorMessage}>
            {error.receives_subscription_messages[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleAddUserSubmit}
          loading={loading}
        >
          {t("Add user")}
        </Button>
      </Box>
    </Box>
  );
}
